class HeaderNavigation extends HTMLElement {
  constructor() {
    super();
    this.activeMainItem = null;
    this.activeSubList = null;
    this.activeSubItem = null;
    this.activeMegaMenu = null;
    this.headerElement = document.getElementById("header");
    this.closeTimeout = null;
    this.debounceDelay = 300;
    this.storageKey = "activeNavigationItem";
  }

  connectedCallback() {
    this.mainNavItems = Array.from(this.querySelectorAll("[data-navigation-source]"));
    this.subNavLists = Array.from(this.querySelectorAll("[data-navigation-target]"));
    this.subNavItems = Array.from(this.querySelectorAll("[data-navigation-sub-source]"));
    this.megaMenus = Array.from(this.querySelectorAll("[data-navigation-sub-target]"));

    this.markNavigationItems();
    this.setupMainNavigation();
    this.setupSubNavigation();
    this.setupMegaMenus();
    this.setupHeaderHover();
    this.setActiveItemFromUrl();
    this.checkActiveItem();

    window.addEventListener("popstate", this.setActiveItemFromUrl.bind(this));
  }

  markNavigationItems() {
    // Mark main navigation items that have corresponding sub navigation
    this.mainNavItems.forEach((item) => {
      const sourceValue = item.dataset.navigationSource;
      const targetSubNav = this.querySelector(`[data-navigation-target="${sourceValue}"]`);

      // If the sub navigation exists and has at least one item
      if (targetSubNav && targetSubNav.querySelectorAll("[data-navigation-sub-source]").length > 0) {
        // Mark this item as having children (for styling purposes)
        item.classList.add("has-children");
      }
    });
  }

  setupHeaderHover() {
    // Check if header element exists
    if (this.headerElement) {
      // Add mouseenter event to add class - only for desktop (991px or higher)
      this.headerElement.addEventListener("mouseenter", () => {
        if (window.innerWidth >= 991) {
          this.headerElement.classList.add("is-megamenu-open");
        }
      });

      // Add mouseleave event to remove class if no menu is active - only for desktop
      this.headerElement.addEventListener("mouseleave", () => {
        if (window.innerWidth >= 991) {
          // Only remove the class if no navigation item is active
          if (!this.activeMainItem) {
            this.headerElement.classList.remove("is-megamenu-open");
          }
        }
      });

      // Add resize event listener to handle responsive behavior
      window.addEventListener("resize", () => {
        if (window.innerWidth < 991) {
          // Remove the class when below desktop breakpoint
          this.headerElement.classList.remove("is-megamenu-open");
        }
      });
    }
  }

  setupMainNavigation() {
    // Add click event listeners to main navigation items to save to sessionStorage
    this.mainNavItems.forEach((item) => {
      const anchor = item.querySelector("a");
      if (anchor) {
        anchor.addEventListener("click", () => {
          this.saveActiveItem(item.dataset.navigationSource);
        });
      }
    });
  }

  setupSubNavigation() {
    this.subNavItems.forEach((item) => {
      // Add hover events to sub navigation items
      item.addEventListener("mouseenter", () => {
        this.handleSubItemHover(item);
      });

      item.addEventListener("mouseleave", () => {
        this.debouncedCloseMegaMenu();
      });

      // Add click events to sub navigation items
      if (!item.querySelector("a")) {
        item.addEventListener("click", () => {
          this.handleSubItemClick(item);
        });
      }
    });

    // Add mouseleave event to the entire navigation to close mega menus
    this.addEventListener("mouseleave", () => {
      this.closeAllMegaMenus();
    });
  }

  setupMegaMenus() {
    // Add hover events to mega menus to keep their sub items active
    this.megaMenus.forEach((menu) => {
      menu.addEventListener("mouseenter", () => {
        // Cancel any pending close operation
        this.cancelCloseTimeout();

        const subTargetValue = menu.dataset.navigationSubTarget;
        // Find the corresponding sub item
        const subItem = this.querySelector(`[data-navigation-sub-source="${subTargetValue}"]`);
        if (subItem) {
          this.setActiveSubItem(subItem);
        }
      });

      menu.addEventListener("mouseleave", () => {
        this.debouncedCloseMegaMenu();
      });
    });
  }

  handleSubItemHover(item) {
    // Cancel any pending close operation
    this.cancelCloseTimeout();

    const subSourceValue = item.dataset.navigationSubSource;

    // If this is a different item than what's currently active, close any open mega menu
    if (this.activeSubItem !== item) {
      this.closeAllMegaMenusImmediately();
    }

    // Set active sub item and open corresponding mega menu
    this.setActiveSubItem(item);
    this.openMegaMenu(subSourceValue);
  }

  handleSubItemClick(item) {
    // Cancel any pending close operation
    this.cancelCloseTimeout();

    const subSourceValue = item.dataset.navigationSubSource;
    this.openMegaMenu(subSourceValue);
  }

  debouncedCloseMegaMenu() {
    // Clear any existing timeout to prevent duplicate timeouts
    this.cancelCloseTimeout();

    // Set a new timeout
    this.closeTimeout = setTimeout(() => {
      this.closeAllMegaMenusImmediately();
    }, this.debounceDelay);
  }

  cancelCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  setActiveMainItem(item) {
    // Remove active class from all main items
    this.mainNavItems.forEach((navItem) => {
      navItem.classList.remove("header-navigation__main__list-item--active");
    });

    // Set new active item
    if (item) {
      item.classList.add("header-navigation__main__list-item--active");
      this.activeMainItem = item;

      // Add is-megamenu-open class to header
      if (this.headerElement) {
        this.headerElement.classList.add("is-megamenu-open");
      }

      // Save to session storage
      this.saveActiveItem(item.dataset.navigationSource);
    } else {
      this.activeMainItem = null;

      // Remove is-megamenu-open class from header
      if (this.headerElement) {
        this.headerElement.classList.remove("is-megamenu-open");
      }
    }
  }

  setActiveSubItem(item) {
    // Remove active class from all sub items
    this.subNavItems.forEach((navItem) => {
      navItem.classList.remove("header-navigation__sub__list-item--active");
    });

    // Set new active sub item
    if (item) {
      item.classList.add("header-navigation__sub__list-item--active");
      this.activeSubItem = item;
    } else {
      this.activeSubItem = null;
    }
  }

  openSubNav(sourceValue) {
    // Hide all sub navigation lists
    this.subNavLists.forEach((list) => {
      list.classList.remove("header-navigation__sub__list--active");
    });

    // Find and show the matching sub navigation list
    const targetSubNav = this.querySelector(`[data-navigation-target="${sourceValue}"]`);
    if (targetSubNav) {
      targetSubNav.classList.add("header-navigation__sub__list--active");
      this.activeSubList = targetSubNav;
    }
  }

  openMegaMenu(subSourceValue) {
    // We don't close all mega menus here anymore, to allow for debounced switching
    // Instead, we just close the current one if it's different

    // Find and show the matching mega menu
    const targetMegaMenu = this.querySelector(`[data-navigation-sub-target="${subSourceValue}"]`);
    if (targetMegaMenu) {
      // If this is a different mega menu than what's currently active, close the current one
      if (this.activeMegaMenu && this.activeMegaMenu !== targetMegaMenu) {
        this.activeMegaMenu.classList.remove("header-navigation__mega-menu--active");
      }

      targetMegaMenu.classList.add("header-navigation__mega-menu--active");
      this.activeMegaMenu = targetMegaMenu;

      // Add class to header for browsers without :has() support
      if (this.headerElement) {
        this.headerElement.classList.add("has-active-megamenu");
      }
    }
  }

  closeAllMegaMenusImmediately() {
    this.megaMenus.forEach((menu) => {
      menu.classList.remove("header-navigation__mega-menu--active");
    });
    this.setActiveSubItem(null);
    this.activeMegaMenu = null;

    // Remove class from header for browsers without :has() support
    if (this.headerElement) {
      this.headerElement.classList.remove("has-active-megamenu");
    }
  }

  closeAllMegaMenus() {
    // Cancel any pending timeout
    this.cancelCloseTimeout();

    // Close immediately without debounce
    this.closeAllMegaMenusImmediately();
  }

  closeAll() {
    // Close all sub navigation and mega menus
    this.subNavLists.forEach((list) => {
      list.classList.remove("header-navigation__sub__list--active");
    });

    this.closeAllMegaMenus();
    this.setActiveMainItem(null);
    this.activeSubList = null;
  }

  setActiveItemFromUrl() {
    const pathname = window.location.pathname;

    // Skip on frontpage
    if (this.isFrontpage(pathname) && !window.indexHeaderNavigationFallback) {
      // Reset everything on the frontpage
      this.closeAll();
      return;
    }


    let key = this.getKeyFromPathname(pathname);
    let matchingItem = null;

    if (key) {
      // Find matching main navigation item from URL
      matchingItem = this.mainNavItems.find((item) => item.dataset.navigationSource === `navigation-${key}`);
    }

    // If no match found from URL, try to use the fallback metafield
    if (!matchingItem) {
      const fallbackMetafield = this.dataset.navigationFallbackMetafield;
      if (fallbackMetafield) {
        matchingItem = this.mainNavItems.find((item) => item.dataset.navigationSource === fallbackMetafield);
      }
    }

    // If still no match, try to find an item with data-navigation-fallback="true"
    if (!matchingItem) {
      matchingItem = this.mainNavItems.find(
        (item) => item.dataset.navigationFallback === "true" || item.dataset.navigationFallback === true
      );
    }

    // If still no match, try to get from session storage
    if (!matchingItem) {
      const savedKey = this.getSavedActiveItem();
      if (savedKey) {
        matchingItem = this.mainNavItems.find((item) => item.dataset.navigationSource === savedKey);
      }
    }


    //TODO: If matching item is found, sometimes the real active item is not set, but the item with matchingItem. This can be resolved with not having submenu set as "Use as fallback"
    if (matchingItem) {
      if (this.isFrontpage(pathname) && window.indexHeaderNavigationFallback) {
      } else{
      this.setActiveMainItem(matchingItem);
      }
      this.openSubNav(matchingItem.dataset.navigationSource);
    }
  }

  checkActiveItem() {
    const activeSubNav = this.querySelector('.header-navigation__sub__list--active');
    if (activeSubNav && activeSubNav.childNodes.length < 2) {
      let matchingItem = null;

      if (!matchingItem) {
        const fallbackMetafield = this.dataset.navigationFallbackMetafield;
        if (fallbackMetafield) {
          matchingItem = this.mainNavItems.find((item) => item.dataset.navigationSource === fallbackMetafield);
        }
      }
  
      if (!matchingItem) {
        matchingItem = this.mainNavItems.find(
          (item) => item.dataset.navigationFallback === "true" || item.dataset.navigationFallback === true
        );
      }

      if (matchingItem) {
        this.openSubNav(matchingItem.dataset.navigationSource);
      }
    }
  }

  isFrontpage(pathname) {
    // Check if we're on the frontpage (home page)
    return pathname === "/" || pathname === "" || pathname === "/index" || pathname === "/index.html";
  }

  saveActiveItem(sourceValue) {
    // Don't save if we're on the frontpage
    if (this.isFrontpage(window.location.pathname)) {
      return;
    }

    try {
      sessionStorage.setItem(this.storageKey, sourceValue);
    } catch (e) {
      console.warn("Session storage is not available:", e);
    }
  }

  getSavedActiveItem() {
    // Don't retrieve saved item if we're on the frontpage
    if (this.isFrontpage(window.location.pathname)) {
      return null;
    }

    try {
      return sessionStorage.getItem(this.storageKey);
    } catch (e) {
      console.warn("Session storage is not available:", e);
      return null;
    }
  }

  getKeyFromPathname(pathname) {
    // Extract the last segment after the last "/"
    const pathSegments = pathname.split("/");
    const lastSegment = pathSegments[pathSegments.length - 1];

    // If there's no last segment, return null
    if (!lastSegment) {
      if (this.isFrontpage(pathname)) {
        if (window.indexHeaderNavigationFallback) {
          return window.indexHeaderNavigationFallback;
        }
      }
      return null;
    } 


    // Get segment before any underscore
    return lastSegment.split("_")[0];
  }
}

// Register the custom element
customElements.define("header-navigation", HeaderNavigation);
